import {
  COMPARISON_DEFAULT_SORT_FIELD,
  COMPARISON_DEFAULT_TOP_COUNT,
  COMPARISON_SECONDARY_SORT,
  COMPARISON_SORT_CAPTIONS,
  type ReportingComparisonRowDto,
  type ReportingComparisonRowsHttpBody,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import type { ProductionComparisonRowsService } from './production-comparison-rows.service';

/** How many rows a table starts with, and how many each Load more adds. */
export const COMPARISON_TABLE_PAGE_SIZES = [5, 10, 15, 20, 25] as const;
export const COMPARISON_TABLE_DEFAULT_PAGE_SIZE = COMPARISON_DEFAULT_TOP_COUNT;

/** The full-list tables open wider: they are the ones read for a whole picture, not a top few. */
export const COMPARISON_ALL_TABLE_PAGE_SIZES = [10, 15, 25, 30] as const;
export const COMPARISON_ALL_TABLE_DEFAULT_PAGE_SIZE = 15;

export type ComparisonTableDirection = 'increase' | 'decrease';

/**
 * What a mover table remembers that the grid does not.
 *
 * DevExtreme saves the columns, and the order is one of the things it saves, so the order is
 * not repeated here. It was, and the two drifted: the grid restored the column the viewer had
 * sorted by while this restored the one before it, and whichever was read last won.
 *
 * What is left is what the grid has no notion of — the size the table opens at, and how far it
 * had been grown.
 */
export interface ComparisonTablePersistedConfig {
  pageSize: number;
  loadedCount: number;
}

/**
 * Sortable columns, by the row field they order, and the column a table opens on.
 *
 * The catalog's, so the reporting API names a printed table's order exactly as the page does.
 */
export const COMPARISON_TABLE_SORT_CAPTIONS: Readonly<Record<string, string>> = COMPARISON_SORT_CAPTIONS;

export const COMPARISON_TABLE_DEFAULT_SORT_FIELD: string = COMPARISON_DEFAULT_SORT_FIELD;

/** Ties break by name, ascending, always — see COMPARISON_SECONDARY_SORT. */
const SECONDARY_SORT = COMPARISON_SECONDARY_SORT;

/** What the grid reports once remote operations are on; only these two are its to decide. */
export interface ComparisonGridLoadOptions {
  sort?: unknown;
  filter?: unknown;
  /** Present when a header filter is asking for a column's values rather than for rows. */
  group?: unknown;
}

/**
 * The first column the grid sorted by that these tables can actually order on.
 *
 * Unsortable columns are skipped rather than refused outright: the tiebreak below is appended
 * to every request, so a grid reporting it alongside the viewer's column must not count as the
 * order that was asked for.
 */
function readPrimarySort(sort: unknown): { selector: string; desc: boolean } | null {
  const entries = Array.isArray(sort) ? sort : sort ? [sort] : [];

  for (const entry of entries) {
    const selector = (entry as { selector?: unknown })?.selector;
    if (typeof selector === 'string' && COMPARISON_TABLE_SORT_CAPTIONS[selector]) {
      return { selector, desc: (entry as { desc?: boolean })?.desc === true };
    }
  }

  return null;
}

/**
 * One table's rows, loaded a page at a time.
 *
 * The page size does double duty, as the section's Rows picker always implied: it is both how
 * many rows the table opens with and how many each Load more brings in. Rows accumulate rather
 * than replace, so pressing Load more reads as the table growing rather than turning a page.
 */
export class ComparisonTableState {
  rows: ReportingComparisonRowDto[] = [];
  totalCount = 0;
  isLoading = false;
  /** Set when a load failed, so the table can offer the press again instead of going silent. */
  errorMessage: string | null = null;
  /** Until this table has fetched once, `totalCount` says nothing about what is left. */
  hasLoaded = false;

  constructor(
    private readonly rowsService: ProductionComparisonRowsService,
    private readonly buildBody: () => Omit<ReportingComparisonRowsHttpBody, 'loadOptions'>,
    private readonly direction: ComparisonTableDirection,
    public pageSize: number = COMPARISON_TABLE_DEFAULT_PAGE_SIZE,
    /** Lets the table show the grid's own load panel for as long as a page is in flight. */
    private readonly onLoadingChange?: (isLoading: boolean) => void,
    /** Called whenever something worth remembering changes, so the page can store it. */
    private readonly onConfigChanged?: () => void,
  ) {}

  /**
   * Whether Load more is worth offering. Before the first fetch the answer is yes: the table is
   * showing the movers that came with the page, which are cut to the top count, and how many
   * more exist is exactly what the first press finds out.
   */
  /** The column the table is ordered by. Changing it re-opens the table on the new order. */
  sortField: string = COMPARISON_TABLE_DEFAULT_SORT_FIELD;

  get sortDescending(): boolean {
    return this.sortDesc ?? this.direction === 'increase';
  }

  get sortCaption(): string {
    return (
      COMPARISON_TABLE_SORT_CAPTIONS[this.sortField] ??
      COMPARISON_TABLE_SORT_CAPTIONS[COMPARISON_TABLE_DEFAULT_SORT_FIELD]
    );
  }

  private sortDesc: boolean | null = null;
  /** How many rows the first load should fetch, when reopening a table that had been grown. */
  private pendingRestoreCount: number | null = null;
  /** The grid's filter, sent with every request — these tables hold a page, not the set. */
  private filter: unknown = null;

  get hasFilter(): boolean {
    return this.filter !== null && this.filter !== undefined;
  }

  /**
   * One column's distinct values, for a header filter.
   *
   * Asked of the endpoint rather than answered from `rows`, which is the whole point: the list
   * is every name in the set, not the fifteen the table is holding. Nothing here is kept — a
   * header filter opening is not the table changing what it shows.
   */
  async loadGroupCounts(loadOptions: ComparisonGridLoadOptions): Promise<unknown[]> {
    const response = await this.rowsService.getRows({
      ...this.buildBody(),
      loadOptions: {
        group: loadOptions.group,
        ...(loadOptions.filter ? { filter: loadOptions.filter } : {}),
      } as ReportingComparisonRowsHttpBody['loadOptions'],
    });

    return response.data as unknown[];
  }

  /**
   * The grid's load options, turned into the order and the filter the server is asked for.
   *
   * The grid reports what the viewer clicked and renders whatever comes back; it no longer
   * orders anything itself. That is the point of asking the server: a table showing five of a
   * hundred and thirty rows can only sort them by asking for a different five, and sorting the
   * five it holds would reorder those and leave the rest where they were.
   *
   * A no-op when neither changed, so a grid re-emitting its own state does not refetch.
   */
  async applyGridLoadOptions(loadOptions: ComparisonGridLoadOptions, onSettled?: () => void): Promise<void> {
    const primary = readPrimarySort(loadOptions?.sort);
    const nextFilter = loadOptions?.filter ?? null;

    const isSortChanged = !!primary && (primary.selector !== this.sortField || primary.desc !== this.sortDescending);
    const isFilterChanged = JSON.stringify(nextFilter) !== JSON.stringify(this.filter);

    if (isSortChanged) {
      this.sortField = primary.selector;
      this.sortDesc = primary.desc;
    }

    if (isFilterChanged) {
      this.filter = nextFilter;
    }

    if (!this.hasLoaded || isSortChanged || isFilterChanged) {
      await this.reload(onSettled);
      return;
    }

    onSettled?.();
  }

  get canLoadMore(): boolean {
    return !this.hasLoaded || this.rows.length < this.totalCount;
  }

  /** How many the next press brings in — the page size, or whatever is actually left. */
  get nextPageSize(): number {
    if (!this.hasLoaded) {
      return this.pageSize;
    }

    return Math.min(this.pageSize, Math.max(this.totalCount - this.rows.length, 0));
  }

  /** How many rows remain beyond the ones on screen; null until the table has fetched once. */
  get remainingCount(): number | null {
    return this.hasLoaded ? Math.max(this.totalCount - this.rows.length, 0) : null;
  }

  /**
   * Every row this table has, regardless of what is on screen.
   *
   * For the export, which is about the table's whole set rather than the page of it someone
   * happens to have scrolled to. Fetched fresh rather than from `rows`, which holds only what
   * has been loaded so far.
   */
  async fetchAllRows(): Promise<ReportingComparisonRowDto[]> {
    const response = await this.rowsService.getRows({
      ...this.buildBody(),
      loadOptions: {
        sort: [{ selector: this.sortField, desc: this.sortDescending }, SECONDARY_SORT],
        ...(this.filter ? { filter: this.filter } : {}),
        skip: 0,
        take: Math.max(this.totalCount, this.rows.length, 1),
      },
    });

    return response.data;
  }

  /**
   * Back to how the table ships: its opening column, its own direction and its opening size.
   *
   * The grid cannot do this alone. Clearing a DevExtreme state leaves a grid with no sort at
   * all, while the order it is actually showing lives here and is decided by the server — so a
   * reset that only cleared the grid would leave the two disagreeing.
   */
  async resetToDefaults(defaultPageSize: number, onSettled?: () => void): Promise<void> {
    this.sortField = COMPARISON_TABLE_DEFAULT_SORT_FIELD;
    this.sortDesc = null;
    this.pageSize = defaultPageSize;
    this.pendingRestoreCount = null;
    this.filter = null;
    await this.reload(onSettled);
  }

  /** True when nothing here has been changed from how the table ships. */
  isDefaultConfig(defaultPageSize: number): boolean {
    return this.pageSize === defaultPageSize && !this.hasFilter && this.rows.length <= defaultPageSize;
  }

  toPersistedConfig(): ComparisonTablePersistedConfig {
    return {
      pageSize: this.pageSize,
      loadedCount: this.rows.length,
    };
  }

  /**
   * Applied before the first load, so the table opens showing what it was left showing rather
   * than opening small and jumping once the stored size is applied.
   */
  restoreConfig(config: ComparisonTablePersistedConfig): void {
    this.pageSize = config.pageSize > 0 ? config.pageSize : this.pageSize;
    this.pendingRestoreCount = config.loadedCount > this.pageSize ? config.loadedCount : null;
  }

  /** Starts the table over: a new comparison means these rows describe nothing. */
  reset(): void {
    this.rows = [];
    this.totalCount = 0;
    this.errorMessage = null;
    this.hasLoaded = false;
  }

  async reload(onSettled?: () => void): Promise<void> {
    const restoreCount = this.pendingRestoreCount;
    this.pendingRestoreCount = null;
    this.reset();
    await this.loadPage(0, onSettled, restoreCount ?? undefined);
  }

  async loadMore(onSettled?: () => void): Promise<void> {
    // The one case where dropping is right: a second press mid-flight would fetch and append
    // the page already on its way.
    if (this.isLoading) {
      return;
    }

    await this.loadPage(this.rows.length, onSettled);
  }

  /** Changing the page size re-opens the table at that size rather than topping it up. */
  async setPageSize(pageSize: number, onSettled?: () => void): Promise<void> {
    if (pageSize === this.pageSize) {
      return;
    }

    this.pageSize = pageSize;
    await this.reload(onSettled);
  }

  /**
   * Each load takes a token, and only the newest one may write.
   *
   * Refusing to start while another load is in flight looked safer and was not: changing the
   * page size during the opening fetch left the new size set and the old rows on screen,
   * because the reload it asked for was dropped. Superseding instead means the last thing
   * asked for is always what arrives.
   */
  private requestToken = 0;

  private async loadPage(skip: number, onSettled?: () => void, takeOverride?: number): Promise<void> {
    const token = ++this.requestToken;
    this.isLoading = true;
    this.errorMessage = null;
    this.onLoadingChange?.(true);

    try {
      const response = await this.rowsService.getRows({
        ...this.buildBody(),
        loadOptions: {
          // Increases and decreases are the same rows read from opposite ends.
          sort: [{ selector: this.sortField, desc: this.sortDescending }, SECONDARY_SORT],
          ...(this.filter ? { filter: this.filter } : {}),
          skip,
          take: takeOverride ?? this.pageSize,
        },
      });

      if (token !== this.requestToken) {
        return;
      }

      this.rows = skip === 0 ? response.data : [...this.rows, ...response.data];
      this.totalCount = response.totalCount;
      this.hasLoaded = true;
      this.onConfigChanged?.();
    } catch (error) {
      if (token === this.requestToken) {
        console.error('[ProductionComparison] Failed to load table rows', error);
        this.errorMessage = 'We could not load more rows. Try again in a moment.';
      }
    } finally {
      if (token === this.requestToken) {
        this.isLoading = false;
        this.onLoadingChange?.(false);
      }
      onSettled?.();
    }
  }
}
